import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { UpperCasePipe } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { LanguageService } from "../../core/services/language.service";
import { SeoService } from "../../core/services/seo.service";
import { BLOG_ARTICLES } from "../../core/constants/page-content.constants";
import { APP_NAME, APP_CONSTANTS } from "../../core/constants/app.constants";
import { HeroComponent } from "./hero.component";

@Component({
  selector: "app-landing",
  standalone: true,
  imports: [RouterLink, UpperCasePipe, HeroComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./landing.component.html",
  styleUrl: "./landing.component.scss",
})
export class LandingComponent {
  readonly appName = APP_NAME;
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  readonly copy = inject(LanguageService).copy;
  readonly blogArticles = BLOG_ARTICLES;
  readonly siteUrl = APP_CONSTANTS.siteUrl;

  constructor() {
    const description =
      "Talk to strangers online with free anonymous random text chat. Meet people worldwide through shared interests, without creating a public profile.";
    this.seo.updatePage({
      title: `Talk to Strangers Online | Free Anonymous Random Chat | ${APP_NAME}`,
      description,
      canonical: `${APP_CONSTANTS.siteUrl}/`,
      hreflangSlug: "",
    });
    this.seo.setPageJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Can I chat with strangers online without signing up?",
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes. ${APP_NAME} lets guests start an anonymous random chat without creating an account. Just pick a nickname and start talking.`,
          },
        },
        {
          "@type": "Question",
          name: `Is ${APP_NAME} a free Omegle alternative?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes. ${APP_NAME} is a free anonymous text chat platform that works as a friendly Omegle alternative with interest-based matching and no account required.`,
          },
        },
        {
          "@type": "Question",
          name: "How does interest-based matching work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: `Add topics you enjoy — gaming, music, travel, books — and ${APP_NAME} prioritises matching you with someone who shares them. The first message always has a natural starting point.`,
          },
        },
        {
          "@type": "Question",
          name: "Can I make friends from other countries?",
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes. ${APP_NAME} connects you with people worldwide through shared interests, making international conversations easy and natural.`,
          },
        },
        {
          "@type": "Question",
          name: `Is ${APP_NAME} a video chat website?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `${APP_NAME} is primarily a text chat platform. Video chat is available as an additional feature. Both are free and require no account.`,
          },
        },
        {
          "@type": "Question",
          name: `Do I need to create an account to use ${APP_NAME}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: "No account is needed. Choose a nickname, select your interests, and start chatting instantly as a guest.",
          },
        },
      ],
    });
  }

  startChat() {
    this.router.navigateByUrl("/chat");
  }

  startVideo() {
    this.router.navigateByUrl("/video");
  }
}
